import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Users, Loader2, Settings, CheckCircle2, Lock, LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { sanitizeHtml } from '../lib/sanitizeHtml';
import { setFavicon } from '../lib/favicon';

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export default function SubgroupHome() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { getSubgroup, joinSubgroup, user, org } = useAuth();
  const [sg, setSg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    setLoading(true); setNotFound(false);
    getSubgroup(slug).then(setSg).catch(() => setNotFound(true)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  // Swap the browser-tab favicon to this subgroup's while viewing it; restore org's on leave.
  useEffect(() => {
    if (sg && sg.favicon_url) setFavicon(sg.favicon_url);
    return () => setFavicon(org && org.favicon_url);
  }, [sg, org]);

  const join = async () => {
    setErr(''); setBusy(true);
    try {
      const res = await joinSubgroup(slug);
      if (res.paid && res.checkout_url) { window.location.href = res.checkout_url; return; }
      load();
    } catch (e) {
      setErr((e.response && e.response.data && e.response.data.detail) || 'Could not join this group.');
    } finally { setBusy(false); }
  };

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-16 flex items-center gap-2" style={{ color: 'var(--olive)' }}><Loader2 className="animate-spin" size={16} /> Loading…</div>;
  if (notFound || !sg) return <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="subgroup-notfound"><h1 className="font-title text-2xl" style={{ color: 'var(--maroon)' }}>Group not found</h1><Link to="/groups" className="text-sm underline">Back to Groups</Link></div>;

  const m = sg.my_membership || {};
  const isMember = m.is_member;
  const isFinancial = m.status === 'financial';

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 fade-in" data-testid="subgroup-home">
      <Link to="/groups" className="text-sm underline mb-3 inline-block" style={{ color: 'var(--olive)' }}>← All groups</Link>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            {sg.favicon_url ? <img src={sg.favicon_url} alt="" className="w-7 h-7 rounded" data-testid="subgroup-favicon" /> : <Users style={{ color: 'var(--maroon)' }} />}
            <h1 className="font-title text-3xl md:text-4xl" style={{ color: 'var(--maroon)' }} data-testid="subgroup-name">{sg.name}</h1>
          </div>
          {sg.tagline && <p className="text-[15px] mt-1" style={{ color: 'var(--olive)' }}>{sg.tagline}</p>}
        </div>
        <div className="flex items-center gap-2">
          {sg.is_group_admin && (
            <Link to={`/g/${slug}/manage`} data-testid="subgroup-manage-link"
              className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>
              <Settings size={15} /> Manage
            </Link>
          )}
        </div>
      </div>
      <div className="h-1 w-20 my-5" style={{ background: 'var(--gold)' }} />

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          {sg.intro && <p className="text-[15px] mb-4" style={{ color: 'var(--ink)' }}>{sg.intro}</p>}
          {sg.body && <div className="prose prose-sm max-w-none" data-testid="subgroup-body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(sg.body) }} />}
          {!sg.intro && !sg.body && <p className="text-sm" style={{ color: 'var(--olive)' }}>Welcome to {sg.name}.</p>}
        </div>

        <aside className="space-y-4">
          <div className="bg-white rounded-lg border shadow-sm p-4" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="subgroup-join-card">
            <p className="text-sm mb-1" style={{ color: 'var(--olive)' }}>{sg.member_count} member{sg.member_count === 1 ? '' : 's'}</p>
            <p className="font-title text-lg mb-3" style={{ color: 'var(--navy)' }}>{sg.join_fee_cents > 0 ? `${money(sg.join_fee_cents)} / year` : 'Free to join'}</p>
            {isMember ? (
              <div className="inline-flex items-center gap-2 text-sm font-title" style={{ color: '#1f7a3f' }} data-testid="subgroup-member-badge">
                <CheckCircle2 size={16} /> You're a member{sg.join_fee_cents > 0 ? (isFinancial ? ' (paid)' : ' — payment due') : ''}
              </div>
            ) : !user ? (
              <Link to="/login" className="w-full inline-flex items-center justify-center gap-2 py-2 rounded font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>
                <LogIn size={15} /> Sign in to join
              </Link>
            ) : (
              <button onClick={join} disabled={busy} data-testid="subgroup-join-btn"
                className="w-full inline-flex items-center justify-center gap-2 py-2 rounded font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy ? 0.7 : 1 }}>
                {busy && <Loader2 size={14} className="animate-spin" />} {sg.join_fee_cents > 0 ? 'Join & pay' : 'Join group'}
              </button>
            )}
            {isMember && sg.join_fee_cents > 0 && !isFinancial && (
              <button onClick={join} disabled={busy} data-testid="subgroup-renew-btn"
                className="w-full mt-2 inline-flex items-center justify-center gap-2 py-2 rounded font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy ? 0.7 : 1 }}>
                {busy && <Loader2 size={14} className="animate-spin" />} Pay membership
              </button>
            )}
            {err && <p className="text-xs mt-2" style={{ color: '#8a1f11' }} data-testid="subgroup-join-error">{err}</p>}
          </div>

          {sg.pages && sg.pages.length > 0 && (
            <nav className="bg-white rounded-lg border shadow-sm p-4" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="subgroup-nav">
              <h3 className="font-title text-sm mb-2" style={{ color: 'var(--navy)' }}>Group pages</h3>
              <ul className="space-y-1">
                {sg.pages.map((p) => (
                  <li key={p.page_key}>
                    <Link to={`/g/${slug}/${p.page_key}`} data-testid={`subgroup-nav-${p.page_key}`}
                      className="flex items-center gap-2 text-sm py-1 hover:underline" style={{ color: 'var(--maroon)' }}>
                      {p.visibility === 'subgroup' && <Lock size={12} style={{ color: 'var(--olive)' }} />}
                      {p.nav_label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </aside>
      </div>
    </div>
  );
}
